// src/pages/RejectList.jsx
import React, { useState, useEffect } from 'react';
import {
  GridComponent, ColumnsDirective, ColumnDirective,
  Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport, Inject
} from '@syncfusion/ej2-react-grids';
import { Header } from '../Components';
import axios from 'axios';
import {
  FaSearch, FaFileExcel, FaFilePdf, FaEye, FaTimes, FaCopy
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useBaseUrl } from '../Contexts/BaseUrlContext';

const rejectGrid = [
  {
    field: 'client_id',
    headerText: 'Client Id.',
    width: 120,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Client Id.</span>,
    template: (props) => (
      <div
        className="text-center cursor-pointer text-blue-500 hover:underline"
        onClick={() => props.viewHandler(props.client_id)}
      >
        {props.client_id}
      </div>
    ),
  },
  {
    field: 'client_name',
    headerText: 'Client',
    width: 200,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Client</span>,
  },
  {
    field: 'phone_num',
    headerText: 'Contact',
    width: 140,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Contact</span>,
    template: (props) => <div className="truncate">{props.phone_num}{props.phone_num2 ? `, ${props.phone_num2}` : ''}</div>
  },
  {
    field: 'address',
    headerText: 'Address',
    width: 180,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Address</span>,
  },
  {
    field: 'loan_type',
    headerText: 'Loan Type',
    width: 160,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Loan Type</span>,
  },
  {
    field: 'required_loan',
    headerText: 'Required Loan',
    width: 140,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Required Loan</span>,
  },
  {
    field: 'sanction_loan',
    headerText: 'Sanction Loan',
    width: 140,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Sanction Loan</span>,
  },
  {
    field: 'loan_status',
    headerText: 'Status',
    width: 130,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Status</span>,
  },
  {
    field: 'bank_finance',
    headerText: 'Bank/Finance',
    width: 160,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Bank/Finance</span>,
  },
  {
    field: 'reference_by',
    headerText: 'Reference By',
    width: 200,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Reference By</span>,
  },
  {
    field: 'Actions',
    headerText: 'Actions',
    width: 160,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Actions</span>,
    template: (props) => (
      <div className="flex items-center justify-center gap-2">
        <button
          className="text-blue-600 hover:text-blue-800 transition-transform transform hover:scale-110 focus:outline-none"
          onClick={() => props.viewHandler(props.client_id)}
          title="View Details"
        >
          <FaEye size={18} />
        </button>

        <button
          className="px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
          onClick={() => props.reopenHandler(props.client_id)}
          title="Reopen"
        >
          Reopen
        </button>
      </div>
    ),
  },
];

const RejectList = () => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();

  // Helper to format today as YYYY-MM-DD
  const getTodayYMD = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    const fetchList = async () => {
      setIsLoading(true);
      setErrorMessage('');
      const token = localStorage.getItem('authToken');
      if (!token) {
        setErrorMessage('Please log in to access this page.');
        navigate('/');
        setIsLoading(false);
        return;
      }

      try {
        const formData = new FormData();
        formData.append('action', 'loan_list');
        formData.append('type', 'Reject File');

        const resp = await axios.post(
          `${baseUrl}bank-system/loan.php`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        console.log('Reject File List Response:', resp.data);

        if (resp.data && resp.data.status === true && Array.isArray(resp.data.data)) {
          setData(resp.data.data);
        } else {
          setData([]);
          setErrorMessage(resp.data?.message || 'Failed to fetch reject file list.');
        }
      } catch (err) {
        console.error('Axios Error fetching reject list:', err.response || err);
        setErrorMessage(err.response?.data?.message || 'Error fetching reject file list.');
        if (err.response?.status === 401) navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    fetchList();
  }, [navigate, baseUrl]);

  // View handler - now navigates to user profile page instead of opening modal
  const viewHandler = (client_id) => {
    if (!client_id) {
      setErrorMessage('Invalid client id.');
      return;
    }
    // navigate to the user profile route you created (e.g., /user/:id)
    navigate(`/user/${client_id}`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch((err) => {
      console.error('Copy failed:', err);
      alert('Copy failed');
    });
  };

  // Reopen handler — sends action=reopen (backend should support)
  const reopenHandler = async (client_id) => {
    if (!window.confirm(`Reopen Client ID ${client_id} (move back to Inquiry)?`)) return;

    setIsLoading(true);
    setErrorMessage('');
    const token = localStorage.getItem('authToken');
    if (!token) {
      setErrorMessage('Please log in to perform this action.');
      setIsLoading(false);
      navigate('/');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('action', 'reopen'); // adjust action name if backend expects different
      formData.append('client_id', client_id);
      formData.append('reopen_date', getTodayYMD());

      const resp = await axios.post(
        `${baseUrl}bank-system/loan.php`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      console.log('Reopen API resp:', resp.data);

      if (resp.data && resp.data.status === true) {
        alert(resp.data.message || 'Client reopened to Inquiry.');
        // update UI locally: set loan_status back to Inquiry
        setData(prev => prev.map(it => it.client_id === client_id ? { ...it, loan_status: 'Inquiry' } : it));
        if (selectedRow && selectedRow.client_id === client_id) {
          setSelectedRow(prev => prev ? { ...prev, loan_status: 'Inquiry' } : prev);
        }
      } else {
        setErrorMessage(resp.data?.message || 'Failed to reopen client.');
      }
    } catch (err) {
      console.error('Reopen error:', err.response || err);
      setErrorMessage(err.response?.data?.message || 'Error while reopening client.');
      if (err.response?.status === 401) navigate('/');
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = data.filter((item) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (item.client_name || '').toLowerCase().includes(q) ||
      (item.client_id || '').toLowerCase().includes(q) ||
      (item.phone_num || '').toLowerCase().includes(q) ||
      (item.reference_by || '').toLowerCase().includes(q)
    );
  });

  const gridData = filtered.map((item) => ({
    ...item,
    viewHandler,
    reopenHandler,
  }));

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fadeIn min-h-screen">
      <Header category="Dashboard" title="Reject File" />

      {errorMessage && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          {errorMessage}
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by client name, phone, id or reference..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 bg-white shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <button
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => document.getElementById('rejectGrid').ej2_instances[0].excelExport()}
            disabled={isLoading}
          >
            <FaFileExcel className="mr-2" /> Export Excel
          </button>
          <button
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => document.getElementById('rejectGrid').ej2_instances[0].pdfExport()}
            disabled={isLoading}
          >
            <FaFilePdf className="mr-2" /> Export PDF
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <GridComponent
          id="rejectGrid"
          dataSource={gridData}
          allowPaging
          allowSorting
          allowExcelExport
          allowPdfExport
          allowFiltering
          allowResizing
          pageSettings={{ pageSize: 10, pageSizes: [10, 20, 50] }}
          filterSettings={{ type: 'Menu', showFilterBarStatus: true }}
        >
          <ColumnsDirective>
            {rejectGrid.map((c, i) => <ColumnDirective key={i} {...c} />)}
          </ColumnsDirective>
          <Inject services={[Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport]} />
        </GridComponent>
      </div>

      {/* Modal */}
      {selectedRow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl flex flex-col">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white z-10 border-b border-gray-200 pb-2">
              <h2 className="text-xl font-bold text-gray-900">Client Details</h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition-colors"
                onClick={() => setSelectedRow(null)}
              >
                <FaTimes size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 text-gray-700">
              <div className="grid grid-cols-2 gap-4">
                <div><strong>Client Id:</strong></div>
                <div className="text-blue-600">{selectedRow.client_id}</div>

                <div><strong>Client UID:</strong></div>
                <div>{selectedRow.client_uid || '-'}</div>

                <div><strong>Client Name:</strong></div>
                <div>{selectedRow.client_name || '-'}</div>

                <div><strong>Contact 1:</strong></div>
                <div>{selectedRow.phone_num || '-'}</div>

                <div><strong>Contact 2:</strong></div>
                <div>{selectedRow.phone_num2 || '-'}</div>

                <div><strong>Address:</strong></div>
                <div>{selectedRow.address || '-'}</div>

                <div><strong>Loan Type:</strong></div>
                <div>{selectedRow.loan_type || '-'}</div>

                <div><strong>Loan Status:</strong></div>
                <div>{selectedRow.loan_status || '-'}</div>

                <div><strong>Required Loan:</strong></div>
                <div>{selectedRow.required_loan || '-'}</div>

                <div><strong>Sanction Loan:</strong></div>
                <div>{selectedRow.sanction_loan || '-'}</div>

                <div><strong>Bank / Finance:</strong></div>
                <div>{selectedRow.bank_finance || '-'}</div>

                <div><strong>Reference By:</strong></div>
                <div>{selectedRow.reference_by || '-'}</div>

                <div><strong>Disbursed Date:</strong></div>
                <div>{selectedRow.disbursed_date && selectedRow.disbursed_date !== '0000-00-00' ? selectedRow.disbursed_date : '-'}</div>

                <div><strong>Remark:</strong></div>
                <div className="col-span-1" dangerouslySetInnerHTML={{ __html: selectedRow.remark || '-' }} />
              </div>

              <div className="mt-3">
                <button
                  className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 mr-2"
                  onClick={() => {
                    copyToClipboard(JSON.stringify(selectedRow, null, 2));
                    alert('Client JSON copied to clipboard');
                  }}
                >
                  Copy JSON
                </button>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2 sticky bottom-0 bg-white border-t border-gray-200 pt-4">
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                onClick={() => setSelectedRow(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RejectList;
